/**
 * The event catalog — the single source of truth for everything the app knows.
 *
 * All user activity is recorded as immutable events in an append-only log
 * (architecture §2.1). Derived state is computed by pure reducers over that
 * log; nothing derived is ever persisted as authoritative.
 *
 * Two rules bind this file and are easy to break by accident:
 *
 * 1. **Behaviour-neutral nouns** (architecture A9/§2.2). The core entity is a
 *    `targetBehavior`, not a `screenHabit`; `directions` and `expressions`,
 *    never `values` or `priorities`. These keys are persisted and synced, so
 *    renaming them later costs a migration for no benefit.
 * 2. **Everything the UI displays must be derivable from the log** (A10). A
 *    feature that reads a fact needs an event that writes it. `plan.surfaced`
 *    exists solely because of this rule.
 *
 * Events are never mutated or deleted. Corrections are new events. A full wipe
 * deletes the whole store — that is a storage operation, not an event.
 */

import * as v from 'valibot';

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

export interface DomainEvent<P = Record<string, unknown>> {
  /** ULID — lexically sortable, globally unique, no coordination needed. */
  id: string;
  type: EventType;
  /** ISO 8601 *with* timezone offset: local time is what insights are about. */
  occurredAt: string;
  /** Random UUID per install; the merge identity for multi-device sync. */
  deviceId: string;
  /** Per-event-type version, so a newer client's log stays readable. */
  schemaVersion: number;
  payload: P;
}

// ---------------------------------------------------------------------------
// Shared value types
// ---------------------------------------------------------------------------

/** How the app came to know about this Catch. See architecture §2.9. */
export const DETECTION_SOURCES = ['self', 'unknown'] as const;
export type DetectionSource = (typeof DETECTION_SOURCES)[number];

/** The three Comeback outcomes. They carry equal dignity — see spec §7. */
export const RESOLUTIONS = ['redirect', 'surf', 'deliberate'] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

/** Stated intention only. Nothing fires when it elapses (spec §6.2). */
export const UNTIL_CHOICES = ['10m', '20m', 'untilEnds', 'noLimit'] as const;
export type UntilChoice = (typeof UNTIL_CHOICES)[number];

/** The weekly self-report scale, used for noticing and choosing alike. */
export const FREQUENCY_RATINGS = ['rarely', 'sometimes', 'often', 'almostAlways'] as const;
export type FrequencyRating = (typeof FREQUENCY_RATINGS)[number];

export const PHASES = ['appLed', 'coLed', 'userLed', 'alumni'] as const;
export type Phase = (typeof PHASES)[number];

export const PLAN_ORIGINS = ['onboarding', 'selfGenerated'] as const;
export type PlanOrigin = (typeof PLAN_ORIGINS)[number];

export const REVISION_ANSWERS = ['keep', 'smaller', 'differentAction', 'differentTrigger'] as const;
export type RevisionAnswer = (typeof REVISION_ANSWERS)[number];

/** H1's two arms (spec §12). Absent outside beta builds. */
export const BETA_ARMS = ['replaced', 'notReplaced'] as const;
export type BetaArm = (typeof BETA_ARMS)[number];

// ---------------------------------------------------------------------------
// Payload schemas
// ---------------------------------------------------------------------------

const nonEmpty = v.pipe(v.string(), v.minLength(1));
const id = v.pipe(v.string(), v.minLength(1), v.maxLength(64));

/**
 * Crockford base32, 26 characters, as ULIDs are defined.
 *
 * Validated rather than trusted because the *ordering* of the log depends on
 * it: the store returns events in primary-key order and reducers read that as
 * chronological. An id of `"z"` sorts after everything ever written and would
 * silently corrupt that order — including a sync outbox watermark, which is a
 * position in exactly this sequence.
 */
const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;
const eventId = v.pipe(v.string(), v.regex(ULID_PATTERN, 'Event id must be a ULID'));

export function isUlid(value: string): boolean {
  return ULID_PATTERN.test(value);
}
/** Free text the user wrote. Capped so one paste cannot bloat the log. */
const freeText = v.pipe(v.string(), v.maxLength(500));

const onboardingCompleted = v.object({
  /** What matters to the user right now. Never called "values" in the UI. */
  directions: v.array(
    v.object({ directionId: id, label: freeText }),
  ),
  /** What living that direction looks like in real life. */
  expressions: v.array(
    v.object({ directionRef: id, label: freeText }),
  ),
  /** Behaviour-neutral by rule: nothing here assumes a phone. */
  targetBehaviors: v.array(v.object({ behaviorId: id, label: freeText })),
  placementRitualDone: v.boolean(),
  betaArm: v.optional(v.picklist(BETA_ARMS)),
  /**
   * Optional, self-reported, never verified — the vocalization adoption signal
   * of architecture §2.10. No microphone is ever requested.
   */
  saidAloud: v.optional(v.boolean()),
});

const planBody = v.object({
  planId: id,
  /** The "if" of the if-then plan. */
  trigger: freeText,
  /** The "then". The bounded ask lives here, not in an app timer. */
  action: freeText,
  /** Coarse context label used for time/situation matching. */
  context: freeText,
  directionRef: v.optional(id),
  origin: v.picklist(PLAN_ORIGINS),
});

const planArchived = v.object({ planId: id });

/**
 * Written every time the resolution screen displays a plan.
 *
 * Without this, "repeatedly surfaced and repeatedly not chosen" (spec §6.3) is
 * not computable: `catch.resolved` records the plan that *was* chosen and
 * nothing at all records the ones passed over.
 */
const planSurfaced = v.object({
  planId: id,
  catchId: id,
  surfaceContext: freeText,
});

const catchLogged = v.object({
  catchId: id,
  /** From the user's current adaptive tag set. */
  triggerTag: nonEmpty,
  /**
   * `unknown` is the honest default, and in v1 nearly always the truth: the app
   * cannot tell a muscle-memory arrival from a deliberate one, and no latency
   * heuristic will be invented to guess (architecture §2.9).
   */
  detectionSource: v.picklist(DETECTION_SOURCES),
});

const catchResolved = v.object({
  catchId: id,
  resolution: v.picklist(RESOLUTIONS),
  planId: v.optional(id),
  /** Deliberate only, optional. Recorded as a stated intention, nothing more. */
  untilChoice: v.optional(v.picklist(UNTIL_CHOICES)),
});

/**
 * User actions only — a custom tag written, a default dismissed.
 *
 * The adaptive tag *ordering* is derived from Catch history by a reducer and
 * never written back; persisting it would make derived state authoritative.
 */
const tagCreated = v.object({ tag: nonEmpty });
const tagHidden = v.object({ tag: nonEmpty });

const checkinMorning = v.object({
  /** "Where might autopilot show up today?" */
  predictedContext: freeText,
  /** "What would you rather choose there?" */
  intendedChoice: v.optional(freeText),
  /** Optional "what matters today?" anchor — a direction, by the naming rule. */
  directionRef: v.optional(id),
});

const checkinEvening = v.object({
  rating: v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(5)),
});

const checkinWeekly = v.object({
  /** Caught yourself *without* opening Comeback. */
  noticingRating: v.picklist(FREQUENCY_RATINGS),
  /** Having caught it, consciously chose what happened next. */
  choosingRating: v.picklist(FREQUENCY_RATINGS),
  /** How much choices reflected what mattered. */
  alignmentRating: v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(5)),
});

const planRevisionOffered = v.object({ planId: id });
const planRevisionAnswered = v.object({
  planId: id,
  answer: v.picklist(REVISION_ANSWERS),
});

/**
 * The single sanctioned re-offer of the placement ritual (spec §14).
 *
 * One event, so a second offer is impossible by construction rather than by
 * someone remembering to be careful.
 */
const placementReoffered = v.object({});

const phaseTransition = v.object({
  fromPhase: v.picklist(PHASES),
  toPhase: v.picklist(PHASES),
});

const programMarker = v.object({});

/**
 * The single source of truth for settings (architecture §2.3). Settings do not
 * also live in the key-value store: two authoritative copies of one fact
 * diverge the first time two devices merge.
 */
const settingsChanged = v.object({
  key: nonEmpty,
  value: v.union([v.string(), v.number(), v.boolean(), v.null()]),
});

// ---------------------------------------------------------------------------
// The catalog
// ---------------------------------------------------------------------------

/**
 * Every event type, its current schema version, and its payload schema.
 *
 * This object is what the event-catalog docs are generated from, and what both
 * write-path and import-path validation consult. Adding a display without
 * adding its event here is the failure mode A10 exists to prevent.
 */
export const EVENT_CATALOG = {
  'onboarding.completed': { version: 1, payload: onboardingCompleted },

  'plan.created': { version: 1, payload: planBody },
  'plan.revised': { version: 1, payload: planBody },
  'plan.archived': { version: 1, payload: planArchived },
  'plan.surfaced': { version: 1, payload: planSurfaced },
  'plan.revisionOffered': { version: 1, payload: planRevisionOffered },
  'plan.revisionAnswered': { version: 1, payload: planRevisionAnswered },

  'catch.logged': { version: 1, payload: catchLogged },
  'catch.resolved': { version: 1, payload: catchResolved },

  'tag.created': { version: 1, payload: tagCreated },
  'tag.hidden': { version: 1, payload: tagHidden },

  'checkin.morning': { version: 1, payload: checkinMorning },
  'checkin.evening': { version: 1, payload: checkinEvening },
  'checkin.weekly': { version: 1, payload: checkinWeekly },

  'placement.reoffered': { version: 1, payload: placementReoffered },

  'phase.advanced': { version: 1, payload: phaseTransition },
  'phase.extended': { version: 1, payload: phaseTransition },

  'program.started': { version: 1, payload: programMarker },
  'program.completed': { version: 1, payload: programMarker },

  'settings.changed': { version: 1, payload: settingsChanged },
} as const;

export type EventType = keyof typeof EVENT_CATALOG;

export const EVENT_TYPES = Object.keys(EVENT_CATALOG) as EventType[];

/** Payload type for a given event type, inferred from its schema. */
export type PayloadOf<T extends EventType> = v.InferOutput<(typeof EVENT_CATALOG)[T]['payload']>;

export type TypedEvent<T extends EventType = EventType> = DomainEvent<PayloadOf<T>> & { type: T };

export function isKnownEventType(type: string): type is EventType {
  return Object.hasOwn(EVENT_CATALOG, type);
}

export function currentSchemaVersion(type: EventType): number {
  return EVENT_CATALOG[type].version;
}

// ---------------------------------------------------------------------------
// Validation at the two boundaries
// ---------------------------------------------------------------------------

/**
 * The envelope, validated loosely on purpose.
 *
 * `looseObject` keeps unknown envelope fields instead of stripping them, so a
 * log written by a newer client survives a round trip through an older one
 * rather than being silently truncated on re-upload.
 */
const envelopeSchema = v.looseObject({
  id: eventId,
  type: nonEmpty,
  occurredAt: v.pipe(v.string(), v.isoTimestamp()),
  // Deliberately only "a non-empty string": deviceId is an opaque merge
  // identity, nothing reads its shape, and pinning it to a UUID would make a
  // future client's different device-identity scheme unmergeable for no gain.
  // The id above is different — the log's ordering rests on its format.
  deviceId: id,
  schemaVersion: v.pipe(v.number(), v.integer(), v.minValue(1)),
  payload: v.record(v.string(), v.unknown()),
});

/**
 * Write path. Our own code produced this, so anything wrong is a bug and should
 * be loud.
 */
export function assertValidEvent(event: unknown): DomainEvent {
  const envelope = v.parse(envelopeSchema, event);
  if (!isKnownEventType(envelope.type)) {
    throw new Error(`Refusing to write unknown event type: ${envelope.type}`);
  }
  const entry = EVENT_CATALOG[envelope.type];
  if (envelope.schemaVersion !== entry.version) {
    throw new Error(
      `Refusing to write ${envelope.type} at schemaVersion ${envelope.schemaVersion}; current is ${entry.version}`,
    );
  }
  v.parse(entry.payload, envelope.payload);
  return envelope as DomainEvent;
}

export type ForeignEventOutcome =
  | { ok: true; event: DomainEvent }
  /** Keep it, but no reducer here understands it. Skip, never crash. */
  | { ok: false; reason: 'unknownType' | 'futureVersion'; event: DomainEvent }
  /** Not an event at all — malformed envelope. Drop it. */
  | { ok: false; reason: 'malformed'; event: null };

/**
 * Import and sync-download path.
 *
 * A synced log may come from a newer client, so this is deliberately tolerant:
 * unknown types and higher schema versions are *accepted into the log and
 * skipped by reducers*, not rejected. That tolerance is what lets an older
 * device survive a log written by a newer one (architecture §2.2), and it is
 * tested against a deliberately foreign log rather than assumed.
 */
export function acceptForeignEvent(raw: unknown): ForeignEventOutcome {
  const parsed = v.safeParse(envelopeSchema, raw);
  if (!parsed.success) return { ok: false, reason: 'malformed', event: null };

  const envelope = parsed.output as DomainEvent;
  if (!isKnownEventType(envelope.type)) {
    return { ok: false, reason: 'unknownType', event: envelope };
  }
  const entry = EVENT_CATALOG[envelope.type];
  if (envelope.schemaVersion > entry.version) {
    return { ok: false, reason: 'futureVersion', event: envelope };
  }
  const payload = v.safeParse(entry.payload, envelope.payload);
  if (!payload.success) {
    // A known type at a known version whose payload does not validate is
    // corrupt, not futuristic. Dropping it is safer than reducing over it.
    return { ok: false, reason: 'malformed', event: null };
  }
  return { ok: true, event: envelope };
}

/**
 * True when a reducer should look at this event.
 *
 * The one place the tolerance rule is expressed, so no reducer has to remember
 * it independently.
 */
export function isReducible(event: DomainEvent): boolean {
  return isKnownEventType(event.type) && event.schemaVersion <= EVENT_CATALOG[event.type].version;
}
